"use client";

import { useCallback, useEffect, useRef } from "react";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

/** Éléments qu'un usager peut atteindre au clavier à l'intérieur du panneau. */
const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Coquille de boîte de dialogue : fond assombri, panneau centré, verrouillage du
 * défilement, Échap, clic sur le fond, piège de focus.
 *
 * PAS de `createPortal` : le SDK n'importe jamais `react-dom` (le jest de l'admin ne
 * le résout pas depuis `dist`). On suit donc le motif de la visionneuse de galerie,
 * un `position: fixed` posé là où il est écrit — aucun ancêtre du site ne crée de
 * contexte de mise en page qui le clipperait.
 *
 * Fermée, elle ne rend RIEN : le HTML du serveur ne contient jamais de dialogue, ce
 * qui écarte toute divergence d'hydratation.
 *
 * Échelle des plans du site : en-tête 45, menu mobile 46, lien d'évitement 60, plein
 * écran d'un graphique 80, visionneuse de galerie 120. La valeur par défaut, 110,
 * passe donc devant tout sauf la visionneuse — laquelle ne peut pas coexister, elle
 * verrouille elle aussi le défilement et le focus.
 */
export function OverlayDialog({
	open,
	onClose,
	labelledBy,
	zIndex = 110,
	className = "",
	children,
}: {
	open: boolean;
	onClose: () => void;
	/** id du titre du panneau, pour le nom accessible du dialogue. */
	labelledBy: string;
	zIndex?: number;
	/** Classes du PANNEAU (le fond est géré ici). */
	className?: string;
	children: React.ReactNode;
}) {
	const panelRef = useRef<HTMLDivElement | null>(null);
	const previousFocusRef = useRef<HTMLElement | null>(null);

	useBodyScrollLock(open);

	// Mémoriser d'où l'on vient, poser le focus dans le panneau, le rendre à la fermeture :
	// sans cela, refermer renverrait l'usager clavier tout en haut du document.
	useEffect(() => {
		if (!open) return;
		previousFocusRef.current = document.activeElement as HTMLElement | null;
		panelRef.current?.focus();
		return () => {
			previousFocusRef.current?.focus?.();
		};
	}, [open]);

	const onKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === "Escape") {
				event.stopPropagation();
				onClose();
				return;
			}
			if (event.key !== "Tab") return;
			// Piège de focus : la tabulation tourne en boucle dans le panneau tant qu'il est
			// ouvert, sinon on repartirait sur la page en dessous, qui est inerte à l'écran.
			const panel = panelRef.current;
			if (!panel) return;
			const cibles = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
				(element) => element.offsetParent !== null || element === document.activeElement,
			);
			if (cibles.length === 0) {
				event.preventDefault();
				panel.focus();
				return;
			}
			const premier = cibles[0];
			const dernier = cibles[cibles.length - 1];
			const actif = document.activeElement;
			if (!event.shiftKey && (actif === dernier || actif === panel)) {
				event.preventDefault();
				premier.focus();
			} else if (event.shiftKey && (actif === premier || actif === panel)) {
				event.preventDefault();
				dernier.focus();
			}
		},
		[onClose],
	);

	if (!open) return null;

	return (
		<div
			className="fixed inset-0 flex items-center justify-center"
			style={{ zIndex, backgroundColor: "rgba(4, 31, 54, 0.72)" }}
			onMouseDown={(event) => {
				// Uniquement le fond lui-même : un glisser commencé DANS le panneau et relâché
				// dehors ne doit pas fermer.
				if (event.target === event.currentTarget) onClose();
			}}
			onKeyDown={onKeyDown}
		>
			<div
				ref={panelRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={labelledBy}
				tabIndex={-1}
				className={`flex max-h-full w-full flex-col bg-white outline-none ${className}`}
			>
				{children}
			</div>
		</div>
	);
}
